import { createFileRoute, Link } from "@tanstack/react-router";
import { cta } from "@/lib/analytics";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { RelatedLinks } from "@/components/related-links";
import { Reveal, useParallax } from "@/components/reveal";
import { PhotoFigure } from "@/components/photo-figure";


import portrait from "@/assets/sylwia-portrait.jpg.asset.json";
import historia from "@/assets/sylwia-historia.jpg.asset.json";
import teamPhoto from "@/assets/team-photo.jpg.asset.json";
import historiaPortret from "@/assets/sylwia-historia-portret.jpg.asset.json";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs, PERSON_ID, SITE_URL } from "@/content/seo";
import { SESSIONS_LABEL } from "@/content/booking";




const title = "Sylwia Sitkowska: psychologia i biznes | O mnie";
const description =
  "Psycholożka i przedsiębiorczyni. Ponad 10 000 godzin pracy 1:1 i doświadczenie w budowaniu organizacji zatrudniającej setki specjalistów. Poznaj moją drogę.";
const ogImage = abs(ogSocial);

export const Route = createFileRoute("/o-mnie")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "profile" },
      { property: "og:url", content: "https://sylwiasitkowska.pl/o-mnie" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: "https://sylwiasitkowska.pl/o-mnie" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": PERSON_ID,
              name: "Sylwia Sitkowska",
              url: SITE_URL,
              mainEntityOfPage: `${SITE_URL}/o-mnie`,
              image: ogImage,
              sameAs: [
                "https://pl.linkedin.com/in/sylwia-sitkowska",
                "https://www.instagram.com/sitkowska.psycholog/",
                "https://www.youtube.com/@sylwiasitkowska",
                "https://www.przystan-psychologiczna.pl/",
                "https://sylwiasitkowska.pl/media-i-publikacje",
              ],
              jobTitle: "Psycholog dla liderów, psychoterapeutka, przedsiębiorczyni",
              description,
              knowsLanguage: "pl",
              knowsAbout: [
                "psychologia biznesu",
                "wypalenie zawodowe liderów",
                "przywództwo",
                "zarządzanie stresem",
                "podejmowanie decyzji pod presją",
                "psychoterapia CBT",
                "relacje w zespole",
                "delegowanie i granice",
              ],
              hasCredential: [
                { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "Psychologia" },
                { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Psychoterapia CBT" },
              ],
              hasOccupation: {
                "@type": "Occupation",
                name: "Psycholog dla liderów i psychoterapeutka",
                occupationLocation: { "@type": "Country", name: "Polska" },
                skills: "psychologia biznesu, psychoterapia, rozwój liderów, zarządzanie stresem",
              },
              worksFor: {
                "@type": "Organization",
                "@id": "https://www.przystan-psychologiczna.pl/#organization",
                name: "Przystań Psychologiczna",
                url: "https://www.przystan-psychologiczna.pl/",
              },
              alumniOf: [
                { "@type": "CollegeOrUniversity", name: "Uniwersytet SWPS" },
                { "@type": "CollegeOrUniversity", name: "Politechnika Warszawska" },
              ],
              contactPoint: {
                "@type": "ContactPoint",
                contactType: "Kontakt biznesowy",
                availableLanguage: ["pl"],
                url: `${SITE_URL}/kontakt`,
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Strona główna",
                  item: "https://sylwiasitkowska.pl/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "O mnie",
                  item: "https://sylwiasitkowska.pl/o-mnie",
                },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: OMniePage,
});


function OMniePage() {
  const portraitRef = useParallax(18);
  return (

    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section
          id="moja-historia"
          className="relative scroll-mt-24 overflow-hidden border-b border-steel"
        >
          <div aria-hidden="true" className="glow-coral -left-24 -top-24 h-72 w-72" />
          <div className="relative grid lg:grid-cols-[0.85fr_1.15fr]">
            <div className="relative aspect-[4/5] border-b border-steel bg-steel-soft p-0 sm:aspect-[16/10] lg:aspect-auto lg:border-b-0 lg:border-r">
              <PhotoFigure
                src={historia.url}
                alt="Sylwia Sitkowska, psycholog, psychoterapeutka i przedsiębiorczyni"
                figureClassName="h-full"
                className="h-full"
                frameRef={portraitRef}
                imgClassName="h-full w-full object-cover object-[50%_22%] lg:min-h-[34rem]"
                width={849}
                height={1274}
                priority
              />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-surface lg:hidden"
              />
            </div>
            <Reveal className="px-6 py-12 md:px-12 md:py-16">
              <p className="eyebrow">O mnie</p>
              <h1 className="serif-display mt-8 text-[2.2rem] md:text-[3.2rem]">
                Łączę dwa światy, które rzadko spotykają się w{" "}
                <span className="italic text-coral-text">jednym doświadczeniu.</span>
              </h1>

              <div className="mt-8 h-px w-full bg-steel" />
              <div className="measure-text mt-8 space-y-6 text-base leading-relaxed text-ink/80 md:text-lg">
                <p className="drop-cap">
                  Od 16 lat pracuję jako psycholog i psychoterapeutka. Mam za sobą 10 000 h
                  pracy 1:1. Jednocześnie od 2009 roku jestem przedsiębiorczynią. Z jednej
                  poradni powstała organizacja obejmująca dziś 30 placówek i około 300 osób.
                </p>
                <p>
                  Przez te lata zatrudniałam i zwalniałam ludzi, otwierałam kolejne placówki,
                  łączyłam firmy, negocjowałam i podejmowałam decyzje dotyczące dużych pieniędzy.
                  Podejmowałam też złe decyzje. Niektóre sporo mnie kosztowały. Dzisiaj pewnie wiele
                  rzeczy zrobiłabym inaczej. Wtedy nie wiedziałam tego, co wiem dzisiaj.
                </p>
                <p>
                  A równolegle jest życie poza pracą. Jestem matką trójki dzieci. Przez te
                  wszystkie lata łączyłam rozwijanie firm z macierzyństwem, małżeństwem,
                  relacjami, własnymi kryzysami i zmianami. Wiem z własnego doświadczenia, że
                  człowieka nie da się podzielić na część zawodową i prywatną. To, co dzieje się w
                  jednym obszarze życia, prędzej czy później wpływa na pozostałe.
                </p>
                <p>
                  Psychologia nauczyła mnie rozumieć, co dzieje się z człowiekiem. Psychoterapia,
                  słuchać również tego, czego nie mówi wprost. Biznes nauczył mnie podejmować
                  decyzje wtedy, kiedy nie ma jednej dobrej odpowiedzi, brać odpowiedzialność za
                  ich konsekwencje i działać mimo niepewności.
                </p>
                <p>
                  Dzisiaj właśnie z połączenia tych doświadczeń korzystam w pracy z ludźmi, którzy
                  sami odpowiadają za firmy, zespoły i innych ludzi.{" "}
                  Dziś pracuję przede wszystkim z właścicielami firm, liderami i zarządami, łącząc{" "}
                  <Link to="/psychologia-biznesu" className="border-b border-coral text-ink hover:text-coral-text">
                    psychologię biznesu
                  </Link>{" "}
                  z własnym doświadczeniem prowadzenia i skalowania organizacji.
                </p>
              </div>
            </Reveal>

          </div>
        </section>


        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <p className="eyebrow">Skala</p>
            <div className="mt-8 grid grid-cols-3 gap-px overflow-hidden rounded-sm bg-steel/60">
              {[
                ["30", "placówek"],
                ["300", "osób w zespole"],
                ["10 000 h", "pracy 1:1"],
              ].map(([big, small], i) => (
                <Reveal
                  key={small}
                  delay={i * 80}
                  className="bg-surface px-3 py-6 text-center sm:px-4 sm:py-8"
                >
                  <span className="stat-figure block whitespace-nowrap tabular-nums">{big}</span>
                  <span className="mt-2 block text-[0.6rem] uppercase leading-snug tracking-[0.14em] text-ink-soft sm:text-[0.65rem] sm:tracking-[0.18em]">
                    {small}
                  </span>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-10 block">
              <PhotoFigure
                src={teamPhoto.url}
                alt="Zespół 300 specjalistów w 30 placówkach w całej Polsce"
                imgClassName="aspect-[16/7] w-full object-cover"
                width={1920}
                height={775}
              />
            </Reveal>
            <Reveal className="mx-auto mt-10 max-w-3xl text-center">
              <p className="serif-display text-[1.25rem] leading-snug text-ink md:text-[1.6rem]">
                Znam odpowiedzialność lidera nie tylko z gabinetu. Znam ją również z własnego
                życia.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="border-b border-steel">
          <div className="container-page py-12 md:py-16">
            <Reveal>
              <PhotoFigure
                src={historiaPortret.url}
                alt="Sylwia Sitkowska podczas spotkania branżowego"
                imgClassName="aspect-[4/3] w-full object-cover object-[62%_34%] sm:aspect-[16/9] sm:object-[60%_32%]"
                width={1600}
                height={1201}
              />
            </Reveal>
          </div>
        </section>

        <section className="border-b border-steel bg-surface">

          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <p className="eyebrow">Przystań Psychologiczna</p>
            </div>
            <div className="max-w-2xl space-y-6 text-base leading-relaxed text-ink/80 md:text-lg">
              <p data-no-dash-sanitize>
                W 2009 roku założyłam w Warszawie swoją pierwszą poradnię {"\u2013"}{" "}
                <a
                  href="https://www.przystan-psychologiczna.pl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-b border-coral text-ink hover:text-coral-text"
                >
                  Przystań Psychologiczną
                </a>
                . Przez kolejne lata pracowałam jako psychoterapeutka i jednocześnie rozwijałam
                kolejne placówki. Dziś jest ich 30.
              </p>
              <p>Nie wszystko na tej drodze robiłam dobrze.</p>
              <p>
                W 2016 roku dopadło mnie to, co dziś rozpoznaję jako wypalenie. Firma rosła, a ja
                przestałam mieć do niej serce. Wiedziałam, że coś musi się zmienić, ale nie
                wiedziałam jeszcze co.
              </p>
              <p>
                W pewnym momencie wycofałam się z części działań operacyjnych, nie przygotowując
                wcześniej odpowiednio osoby, która mogłaby mnie zastąpić. Firma to przetrwała. Ja
                po jakimś czasie wróciłam. Ale była to jedna z tych lekcji, za które płaci się
                własnym spokojem, relacjami i błędami w biznesie.
              </p>
              <p>
                Pamiętam, jak bardzo brakowało mi wtedy rozmowy z kimś, kto rozumiałby
                jednocześnie człowieka i realia prowadzenia firmy. Nie potrzebowałam kogoś, kto
                powie mi, jak zarządzać. Potrzebowałam kogoś, przy kim mogłabym zrozumieć, co
                właściwie dzieje się ze mną.
              </p>
              <p>Dzisiaj chcę być właśnie taką osobą dla innych liderów.</p>


            </div>
          </div>
        </section>

        <section className="grain bg-ink">
          <div className="container-page py-12 text-mist md:py-16">
            <p className="eyebrow-light">Kolejny krok</p>
            <h2 className="display-xl mt-8 max-w-2xl text-[1.9rem] md:text-[2.6rem]">
              Sprawdźmy, która forma pracy będzie dla Ciebie właściwa
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/konsultacje" {...cta("o-mnie-konsultacje", "domkniecie", "konsultacja")} className="btn-base border border-mist text-mist hover:bg-mist hover:text-ink">
                Konsultacje indywidualne
              </Link>
              <Link to="/mastermind" {...cta("o-mnie-mastermind", "domkniecie", "mastermind")} className="btn-base border border-mist text-mist hover:bg-mist hover:text-ink">
                Mastermind dla liderów, zamknięta grupa
              </Link>
            </div>
          </div>
        </section>
      </main>
      <RelatedLinks exclude={["/o-mnie"]} only={["/konsultacje", "/wystapienia", "/media-i-publikacje"]} heading={"Jak możemy pracować"} />
      <SiteFooter />
    </div>
  );
}
